import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, Target, Heart } from 'lucide-react';
interface OnboardingScreenProps {
  onComplete: () => void;
}
const slides = [{
  icon: <Sparkles className="w-10 h-10" data-id="element-240" />,
  badge: 'Добро пожаловать',
  title: <>
        Настоящие знакомства с{' '}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-svt-pink to-svt-purple" data-id="element-241">
          SVT Dating
        </span>
      </>,
  text: 'SVT помогает выйти за рамки бесконечного свайпа. Вы рассказываете о себе и ставите задачу нашему агенту — он делает всю работу за вас.'
}, {
  icon: <Target className="w-10 h-10" data-id="element-242" />,
  badge: 'Как это работает',
  title: <>
        Ваш персональный{' '}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-svt-pink to-svt-purple" data-id="element-243">
          ИИ-ассистент
        </span>
      </>,
  text: 'Агент подбирает пары по вашим требованиям и отсеивает лишнее. Проведёт 1000 диалогов одновременно и подберёт то, что вы хотите.'
}, {
  icon: <Heart className="w-10 h-10" data-id="element-244" />,
  badge: 'Результат',
  title: <>
        Осталось выбрать{' '}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-svt-pink to-svt-purple" data-id="element-245">
          ресторан
        </span>
      </>,
  text: 'Вам останется лишь собраться на свидание и выбрать ресторан. Никакой рутины — только приятные встречи с подходящими людьми.'
}];
export function OnboardingScreen({
  onComplete
}: OnboardingScreenProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const isLast = currentSlide === slides.length - 1;
  const handleNext = () => {
    if (isLast) {
      onComplete();
    } else {
      setCurrentSlide(prev => prev + 1);
    }
  };
  const slide = slides[currentSlide];
  return <div className="min-h-screen bg-svt-bg flex flex-col relative overflow-hidden" data-id="element-246">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] pointer-events-none" data-id="element-247"></div>
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-gradient-radial from-svt-pink/15 to-transparent rounded-full blur-[80px] pointer-events-none" data-id="element-248"></div>

      {/* Header */}
      <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-249">
        <div className="flex items-center gap-3" data-id="element-250">
          <img src="https://cdn.magicpatterns.com/uploads/x3zgz2YSJzc57ZhT1dBqrP/logo.png" alt="SVT Dating" className="h-8 w-auto" data-id="element-251" />
          <span className="font-serif text-xl font-bold tracking-wide text-svt-text" data-id="element-252">
            SVT dating
          </span>
        </div>
        {!isLast && <button onClick={onComplete} className="text-sm text-svt-text-muted hover:text-svt-text transition-colors" data-id="element-253">
            Пропустить
          </button>}
      </div>

      {/* Slide Content */}
      <div className="flex-1 flex flex-col justify-center px-6 pb-8" data-id="element-254">
        <AnimatePresence mode="wait" data-id="element-255">
          <motion.div key={currentSlide} initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -30
        }} transition={{
          duration: 0.35
        }} className="flex flex-col items-center" data-id="element-256">
            {/* Animated Icon Circle */}
            <div className="relative mb-10" data-id="element-257">
              {/* Outer ring */}
              <div className="w-48 h-48 rounded-full border border-dashed border-svt-pink/20 flex items-center justify-center animate-[spin_25s_linear_infinite]" data-id="element-258">
                <div className="absolute w-3 h-3 rounded-full bg-svt-pink/40 top-0 left-1/2 -translate-x-1/2" data-id="element-259"></div>
              </div>
              {/* Middle ring */}
              <div className="absolute inset-6 rounded-full border border-svt-border flex items-center justify-center" data-id="element-260">
                <div className="absolute inset-0 rounded-full bg-svt-surface backdrop-blur-sm" data-id="element-261"></div>
              </div>
              {/* Inner gradient circle with icon */}
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-262">
                <motion.div animate={{
                scale: [1, 1.1, 1]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="w-20 h-20 rounded-full bg-gradient-primary flex items-center justify-center shadow-[0_0_40px_rgba(232,67,147,0.4)] relative z-10" data-id="element-263">
                  <div className="text-white" data-id="element-264">{slide.icon}</div>
                </motion.div>
              </div>
            </div>

            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-svt-border bg-svt-surface mb-6" data-id="element-265">
              <span className="text-svt-purple text-xs font-bold tracking-widest uppercase" data-id="element-266">
                {slide.badge}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold text-center mb-6 leading-tight text-svt-text" data-id="element-267">
              {slide.title}
            </h1>

            {/* Description */}
            <div className="relative pl-5 border-l-2 border-svt-pink/30 max-w-sm" data-id="element-268">
              <p className="text-svt-text-secondary leading-relaxed text-base font-light" data-id="element-269">
                {slide.text}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom Section */}
      <div className="px-6 pb-10" data-id="element-270">
        {/* Progress Dots */}
        <div className="flex items-center justify-center gap-2 mb-8" data-id="element-271">
          {slides.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'w-8 bg-gradient-primary' : index < currentSlide ? 'w-2 bg-svt-pink/50' : 'w-2 bg-svt-border'}`} data-id="element-272" />)}
        </div>

        {/* CTA Button */}
        <button onClick={handleNext} className="px-12 py-3 rounded-full bg-gradient-primary text-white font-semibold text-base hover:opacity-90 transition-all shadow-[0_0_20px_rgba(232,67,147,0.3)] flex items-center justify-center gap-2 mx-auto" data-id="element-273">
          {isLast ? 'Начать' : 'Далее'}
          <ArrowRight size={20} data-id="element-274" />
        </button>
      </div>
    </div>;
}